import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { apiGet } from '../services/api';
import logger from '../utils/logger';
import { format } from 'date-fns';
import DeadlineHistoryModal from '../components/modals/DeadlineHistoryModal';
import { BsClockHistory, BsPaperclip, BsChatText } from 'react-icons/bs';
import AttachmentsModal from '../components/modals/AttachmentsModal';
import CommentsModal from '../components/modals/CommentsModal';

const TaskTable = ({ title, apiEndpoint }) => {
  const [data, setData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const [deadlineHistoryModal, setDeadlineHistoryModal] = useState({
    visible: false,
    loading: false,
    deadlines: [],
    currentTaskId: null
  });

  const [attachmentsModal, setAttachmentsModal] = useState({
    show: false,
    taskId: null,
    officerNames: '',
    attachments: [],
    onFileDelete: () => { }
  });

  const [commentsModal, setCommentsModal] = useState({
    show: false,
    taskId: null,
    officerNames: '',
    comments: [],
    existingComments: ''
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const toggleExpand = (rowId, columnKey) => {
    const key = columnKey ? `${rowId}-${columnKey}` : rowId;
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleShowDeadlineHistory = async (taskId) => {
    try {
      setDeadlineHistoryModal((prev) => ({
        ...prev,
        visible: true,
        loading: true,
        currentTaskId: taskId,
      }));

      const response = await apiGet(`/TaskTracking/task-deadlines/${taskId}`);

      if (response?.isSuccess && response.data) {
        const sortedDeadlines = response.data.sort(
          (a, b) => new Date(b.changeDate) - new Date(a.changeDate)
        );

        setDeadlineHistoryModal((prev) => ({
          ...prev,
          deadlines: sortedDeadlines,
          loading: false,
        }));
        
      } else {
        setDeadlineHistoryModal((prev) => ({ ...prev, loading: false }));
        toast.error(response?.message || "No deadline history found");
      }
    } catch (error) {
      console.error("Error fetching deadline history:", error);
      setDeadlineHistoryModal((prev) => ({ ...prev, loading: false }));
      toast.error("Failed to load deadline history");
    }
  };

  const handleShowAttachments = async (task) => {
    setAttachmentsModal({
      show: true,
      taskId: task.taskID,
      officerNames: task.officerNames || 'Officer',
      attachments: Array.isArray(task.taskAttachments) ? task.taskAttachments : [],
      onFileDelete: () => {}
    });
  };

  const handleShowComments = async (task, commentType) => {
    try {
      const endpointsToTry = [
        `/Task/GetTaskComments?taskId=${task.taskID}`,
        `/api/TaskComments/${task.taskID}`,
        `/TaskComments/GetByTaskId/${task.taskID}`
      ];

      let response;

      for (const endpoint of endpointsToTry) {
        try {
          response = await apiGet(endpoint);
          if (response) break;
        } catch (err) {
          console.log(`Failed to fetch from ${endpoint}`, err);
        }
      }

      const comments = response?.data || [];

      setCommentsModal({
        show: true,
        taskId: task.taskID,
        officerNames: task.officerNames || 'Officer',
        comments: Array.isArray(comments) ? comments : [],
        existingComments: commentType === 'action' 
          ? (task.actionTakenOrStatus || 'No comments available')
          : (task.additionalComments || 'No comments available')
      });

    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Could not load comments');

      setCommentsModal({
        show: true,
        taskId: task.taskID,
        officerNames: task.officerNames || 'Officer',
        comments: [],
        existingComments: commentType === 'action'
          ? (task.actionTakenOrStatus || 'No comments available')
          : (task.additionalComments || 'No comments available')
      });
    }
  };

const fetchTasks = async () => {
  setIsLoading(true);
  try {
    logger.debug(`Fetching tasks from ${apiEndpoint}`);
    const response = await apiGet(apiEndpoint);
    console.log("Raw API response:", response);
    if (response && Array.isArray(response)) {
      const formattedTasks = response.map(task => ({
        ...task,
        taskAttachments: task.taskAttachments && typeof task.taskAttachments === 'string' 
          ? task.taskAttachments.split(',') 
          : [],
        allDeadline: task.allDeadline || []
      }));
      

      const sortedTasks = formattedTasks.sort((a, b) => {

        const idA = parseInt(a.taskID) || 0;
        const idB = parseInt(b.taskID) || 0;
        return idB - idA; 
      });
      
      setData(sortedTasks);
    } else {
      setData([]);
      toast.error("Invalid data format received from server");
    }
  } catch (error) {
    logger.error('Failed to fetch task data:', error);
    setData([]);
    toast.error(
      <div>
        <strong>Error loading tasks</strong>
        <div>{error.message}</div>
      </div>,
      { autoClose: 7000 }
    );
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    fetchTasks();
  }, [apiEndpoint]);

  const columns = [
    { 
      name: 'Task ID', 
      selector: row => row.taskID || 'N/A', 
      sortable: true,
      width: '130px',
      center: true,
      style: {
        borderRight: '1px solid #dee2e6',
      },
    },
    {
      name: 'Issue, Action or Decision',
      cell: row => {
        const text = row.issueActionOrDecision ? row.issueActionOrDecision.toString() : 'N/A';
        const isExpanded = expandedRows[`${row.taskID}-issue`];
        const shortText = text.length > 100 ? text.slice(0, 100) + '...' : text;

        return (
          <div style={{ minWidth: '200px', whiteSpace: 'pre-wrap' , textAlign: 'left' }}>
            {isExpanded ? text : shortText}
            {text.length > 100 && (
              <span
                onClick={() => toggleExpand(row.taskID, 'issue')}
                  className="p-0 ms-1 view-more"
                    >
                {isExpanded ? 'View Less' : 'View More'}
              </span>
            )}
          </div>
        );
      },
      sortable: true,
      wrap: true,
      minWidth: '270px',
      center: true,
      style: {
        borderRight: '1px solid #dee2e6',
      },
    },
    {
      name: 'Priority',
      selector: row => row.priorityName || 'N/A',
      cell: (row) => {
        const priority = row.priorityName || '';
        let bg = "transparent", color = "#1e293b";
        switch (priority.toLowerCase()) {
          case "top":
             color = "#92400e"; break;
          case "high":
            color = "#7f1d1d"; break;
          case "medium":
            color = "#1e3a8a"; break;
        }
        return (
          <span style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "999px",
            backgroundColor: bg,
            color: color,
            fontWeight: "600",
            fontSize: "13px",
            textTransform: "capitalize",
          }}>
            {priority}
          </span>
        );
      },
      sortable: true,
      width: '140px',
      center: true,
      style: {
        borderRight: '1px solid #dee2e6',
      },
    },
    {
      name: 'Date of Decision',
      cell: row => formatDate(row.dateOfDecisionOrInitiation),
      sortable: true,
      width: '190px',
      center: true,
      style: {
        borderRight: '1px solid #dee2e6',
      },
    },
    {
      name: "Deadline",
      selector: (row) => formatDate(row.deadline),
      cell: (row) => {
        return (
          <div className="d-flex align-items-center">
            <span>{formatDate(row.deadline)}</span>
            <i
              className="bi bi-clock-history ms-2 text-muted cursor-pointer mediumcolor"
              onClick={(e) => {
                e.stopPropagation();
                handleShowDeadlineHistory(row.taskID);
              }}
              title="View deadline history"
              style={{ fontSize: '1.2rem' }}
            />
          </div>
        );
      },
      sortable: true,
      width: '150px',
      center: true,
      style: {
        borderRight: '1px solid #dee2e6',
      },
    },
    {
      name: 'Task Coordinator',
      selector: row => row.coordinatorNames || 'N/A',
      sortable: true,
      width: '210px',
      center: true,
      style: {
        borderRight: '1px solid #dee2e6',
      },
    },
    {
      name: 'Action Taken / Status',
      cell: row => {
        const text = row.actionTakenOrStatus || 'N/A';
        const key = `${row.taskID}-status`;
        const isExpanded = expandedRows[key];
        const shortText = text.length > 100 ? text.slice(0, 100) + '...' : text;
        

        const hasComments = (row.actionTakenOrStatus && row.actionTakenOrStatus !== 'N/A');

        return (
          <div style={{ minWidth: '200px', whiteSpace: 'pre-wrap', textAlign: 'left' }}>
            {isExpanded ? text : shortText}
            {text.length > 100 && (
              <span
                onClick={() => toggleExpand(row.taskID, 'status')}
                   className="p-0 ms-1 view-more"
              >
                {isExpanded ? 'View Less' : 'View More'}
              </span>
            )}
            

            {hasComments && (
              <BsChatText
                className="ms-2 text-muted cursor-pointer mediumcolor"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowComments(row, 'action');
                }}
                title="View comments"
              />
            )}
          </div>
        );
      },
      wrap: true,
      minWidth: '180px',
      center: true,
      style: {
        borderRight: '1px solid #dee2e6',
        textAlign:'left'
      },
    },
  {
    name: <div  style={{textAlign:'center'}}>Officer Responsible</div>,
    cell: row => {
      const hasAttachments = row.taskAttachments && row.taskAttachments.length > 0;
      
      return (
        <div className="d-flex align-items-center">
          <span>{row.officerNames || 'N/A'}</span>
          
          {hasAttachments && (
            <BsPaperclip
              className="ms-2 text-muted cursor-pointer mediumcolor"
              onClick={(e) => {
                e.stopPropagation();
                handleShowAttachments(row);
              }}
              title="View attachments"
            />
          )}
        </div>
      );
    },
    wrap: true,
    width: '200px',
    center: true,
    style: {
      borderRight: '1px solid #dee2e6',
        textAlign:'left'
    },
  },
  {
    name: <div  style={{textAlign:'center'}}>Additional Comments</div> ,
    cell: row => {
      const text = row.additionalComments || 'N/A';
      const key = `${row.taskID}-additional`;
      const isExpanded = expandedRows[key];
      const shortText = text.length > 100 ? text.slice(0, 100) + '...' : text;
      

      const hasComments = (row.additionalComments && row.additionalComments !== 'N/A');

      return (
        <div style={{ minWidth: '200px', whiteSpace: 'pre-wrap' }}>
          {isExpanded ? text : shortText}
          {text.length > 100 && (
            <span
              onClick={() => toggleExpand(row.taskID, 'additional')}
              style={{ color: ' #176B87', cursor: 'pointer', whiteSpace:'nowrap', marginLeft: '5px' }}
            >
              {isExpanded ? 'View Less' : 'View More'}
            </span>
          )}
          
          {hasComments && (
            <BsChatText
              className="ms-2 text-muted cursor-pointer mediumcolor"
              onClick={(e) => {
                e.stopPropagation();
                handleShowComments(row, 'additional');
              }}
              title="View comments"
            />
          )}
        </div>
      );
    },
    wrap: true,
    width: '250px',
    
    style: {
      borderRight: '1px solid #dee2e6',
      textAlign:'left'
    },
  }
  ];

  return (
    <div className="card shadow-sm rounded-4 p-4 border-0 bg-white mb-4">
      <h2 className="page-heading mb-4">{title}</h2>
      <DataTable
        columns={columns}
        data={data.map((dept, idx) => ({
          ...dept,
          _rowIndex: idx, 
        }))}
        pagination
        striped
        responsive
        noDataComponent={<Alert variant="info">No tasks found</Alert>}
        progressPending={isLoading}
        progressComponent={<Spinner animation="border" variant="primary" />}
        className="p-datatable-striped customuser-data-table customarchive-data-table dashboard-table"
        customStyles={{
          table: {
            style: {
              fontWeight: '600',
              fontSize: '14px',
              backgroundColor: '#f8f9fa',
              textAlign: 'center',
            },
          },
          headRow: {
            style: {
              backgroundColor: "#04364A!important",
              minHeight: "48px",
            },
          },
          headCells: {
            style: {
              padding: "10px 14px",
              fontSize: "15px",
              fontWeight: "700",
              color: "white",
              textTransform: "uppercase",
              textAlign: "center",
            },
          },
          cells: {
            style: {
              padding: "10px 14px",
              fontSize: "14px",
              color: "#334155",
            },
          },
        }}
        conditionalRowStyles={[
          {
            when: (row) => row._rowIndex % 2 === 0, 
            style: { backgroundColor: '#f2f2f2' },
          },
          {
            when: (row) => row._rowIndex % 2 !== 0,
            style: { backgroundColor: '#ffffff' },
          },
        ]}  
      />

      <DeadlineHistoryModal
        show={deadlineHistoryModal.visible}
        onHide={() => setDeadlineHistoryModal(prev => ({ ...prev, visible: false }))}
        deadlines={deadlineHistoryModal.deadlines}
        loading={deadlineHistoryModal.loading}
        taskId={deadlineHistoryModal.currentTaskId}
      />

      <AttachmentsModal
        show={attachmentsModal.show}
        onHide={() => setAttachmentsModal(prev => ({ ...prev, show: false }))}
        taskId={attachmentsModal.taskId}
        officerNames={attachmentsModal.officerNames}
        onFileDelete={attachmentsModal.onFileDelete}
      />

      <CommentsModal
        show={commentsModal.show}
        onHide={() => setCommentsModal(prev => ({ ...prev, show: false }))}
        taskId={commentsModal.taskId}
        officerNames={commentsModal.officerNames}
        comments={commentsModal.comments}
        existingComments={commentsModal.existingComments}
      />
    </div>
  );
};

const Notification = () => {
  useEffect(() => {
    const welcomeMessage = "Welcome to the notifications section. Here you can view recent task activities.";
    
    if ('speechSynthesis' in window) {
      const speech = new SpeechSynthesisUtterance();
      speech.text = welcomeMessage;
      
      const voices = window.speechSynthesis.getVoices();
      const femaleVoice = voices.find(voice => 
        voice.name.includes('Female') || 
        voice.name.includes('woman') || 
        voice.name.includes('Zira') 
      );
      
      if (femaleVoice) {
        speech.voice = femaleVoice;
      }
      
      speech.rate = 0.9;
      speech.pitch = 1.1;
      
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(speech);
    }
  }, []);

  return (
    <div className="container py-4">
      <TaskTable title="Task Added within 7 Days" apiEndpoint="/Category/GetAllTaskAddedNotification" />
      <TaskTable title="Task Updated within 7 Days" apiEndpoint="/Category/GetAllTaskUpdatedNotification" />
      <TaskTable title="Task Deleted within 7 Days" apiEndpoint="/Category/GetAllTaskDeletedNotification" />
    </div>
  );
};

export default Notification;